import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  PointerSensor,
  pointerWithin,
  rectIntersection,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from '@dnd-kit/core'
import { CalendarClock, Clock, LayoutGrid, List, PanelRight, Plus, User } from 'lucide-react'
import {
  useBoardDeals,
  useMoveDeal,
  usePipelineDetail,
  usePipelines,
  useSelectedPipeline,
} from '@/features/board/useBoard'
import DealDetail from '@/components/DealDetail'
import { useAuthStore } from '@/features/auth/authStore'
import NextContactDatePrompt from '@/components/NextContactDatePrompt'
import StageRequirementsDialog from '@/components/StageRequirementsDialog'
import PipelineSwitcher from '@/components/PipelineSwitcher'
import DealStatusBadge from '@/components/ui/DealStatusBadge'
import DealTable from '@/components/DealTable'
import NewDealModal from '@/features/deals/NewDealModal'
import TableSettingsPopover from '@/components/TableSettingsPopover'
import CardSettingsPopover from '@/components/CardSettingsPopover'
import { useTableSettings } from '@/features/board/tableSettings'
import { CUSTOM_FIELD_PREFIX, useCardSettings } from '@/features/board/cardSettings'
import { useFieldDefinitions } from '@/features/contacts/useContacts'
import { useMembers } from '@/features/team/useTeam'
import { useCurrency } from '@/features/agency/useCurrency'
import PageContainer from '@/components/layout/PageContainer'
import { daysSince, formatDealValue, formatShortDate, isOverdue, isoInDays, localDate } from '@/lib/dealFormat'
import { formatCustomFieldValue } from '@/lib/customField'
import { missingRequirements, type Requirement } from '@/lib/stageRequirements'
import { apiErrorMessage } from '@/features/settings/apiErrorMessage'
import { saveBoardOrder, type MoveFills } from '@/api/deals'
import { pushToast } from '@/lib/toastStore'
import type { Deal, FieldDefinition, Stage } from '@/api/types'

type BoardViewMode = 'board' | 'table'

/** Board card order within each stage column. Next contact first = "who do I call today". */
type BoardSort = 'nextContact' | 'created' | 'manual'

const VIEW_MODE_STORAGE_KEY = 'pp.boardViewMode'
const BOARD_SORT_STORAGE_KEY = 'pp.boardSort'

/**
 * Orders the cards inside a stage column. `nextContact` (the default) puts the deals due soonest —
 * overdue first — at the top of every column, so the most urgent work is always visible without
 * scrolling; deals with no next contact date sink to the bottom. `created` keeps newest-first.
 */
function compareDeals(a: Deal, b: Deal, sort: BoardSort): number {
  if (sort === 'manual') {
    // "My order": arranged deals in the user's order; ones never arranged follow, newest first.
    const ap = a.boardPosition ?? null
    const bp = b.boardPosition ?? null
    if (ap !== null && bp !== null && ap !== bp) return ap - bp
    if (ap !== null && bp === null) return -1
    if (ap === null && bp !== null) return 1
  }
  if (sort === 'nextContact') {
    const an = a.nextContactDate
    const bn = b.nextContactDate
    if (an && bn && an !== bn) return an < bn ? -1 : 1
    if (an && !bn) return -1
    if (!an && bn) return 1
  }
  return b.createdAt.localeCompare(a.createdAt) // newest first
}

/** Lookup maps for rendering card fields, built once in Board (not per card). */
interface CardRenderContext {
  /** Visible card field ids in display order. */
  fields: string[]
  /** Show each deal's photo across the top of its card. */
  showPhoto: boolean
  /** "My order": a card is also a drop target — a deal dropped on it goes just above it. */
  manualOrder: boolean
  /** userId -> full name, for the 'owner' field. */
  memberNames: Map<string, string>
  /** fieldKey -> definition, for 'custom.<key>' fields. */
  fieldDefsByKey: Map<string, FieldDefinition>
  /** Agency display currency, for the 'value' field. */
  currency: string
}

/** One configurable field line on a deal card; renders nothing when the value is empty. */
function DealCardField({ fieldId, deal, ctx }: { fieldId: string; deal: Deal; ctx: CardRenderContext }) {
  switch (fieldId) {
    case 'value':
      if (deal.value == null) return null
      return <div className="text-xs text-gray-500 mt-1">{formatDealValue(deal.value, ctx.currency)}</div>
    case 'nextContactDate':
      if (!deal.nextContactDate) return null
      return (
        <div
          className={`flex items-center gap-1 text-[11px] mt-1 ${
            isOverdue(deal.nextContactDate) ? 'text-red-600' : 'text-gray-400'
          }`}
        >
          <CalendarClock size={11} className="flex-shrink-0" />
          {formatShortDate(deal.nextContactDate)}
        </div>
      )
    case 'client':
      if (!deal.primaryContactName) return null
      return (
        <div className="flex items-center gap-1 text-xs text-gray-600 mt-1">
          <User size={11} className="flex-shrink-0 text-gray-400" />
          <span className="truncate">{deal.primaryContactName}</span>
        </div>
      )
    case 'lastTouch': {
      // HubSpot's "last activity N ago" chip: amber once the deal has gone quiet for a fortnight.
      const days = deal.lastContactedAt ? daysSince(deal.lastContactedAt) : null
      const stale = days == null || days >= QUIET_DAYS
      return (
        <div className="mt-1.5">
          <span
            title={deal.lastContactedAt ? `Last contact ${formatShortDate(deal.lastContactedAt)}` : 'No contact logged yet'}
            className={`inline-flex items-center gap-1 text-[11px] rounded-md px-1.5 py-0.5 ${stale ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-500'}`}
          >
            <Clock size={10} className="flex-shrink-0" />
            {days == null ? 'No contact yet' : days === 0 ? 'Contacted today' : `Last contact ${days}d ago`}
          </span>
        </div>
      )
    }
    case 'owner': {
      const name = deal.ownerUserId ? ctx.memberNames.get(deal.ownerUserId) : null
      if (!name) return null
      return (
        <div className="flex items-center gap-1 text-xs text-gray-400 mt-1">
          <User size={11} className="flex-shrink-0" />
          <span className="truncate">{name}</span>
        </div>
      )
    }
    case 'created':
      return <div className="text-xs text-gray-400 mt-1">{formatShortDate(deal.createdAt)}</div>
    default: {
      if (!fieldId.startsWith(CUSTOM_FIELD_PREFIX)) return null
      const key = fieldId.slice(CUSTOM_FIELD_PREFIX.length)
      const def = ctx.fieldDefsByKey.get(key)
      if (!def) return null
      const text = formatCustomFieldValue(def, deal.custom[key])
      if (text == null) return null
      // Cards show data only (no field labels), matching the built-in lines; the field name
      // stays available on hover so the value keeps its context.
      return (
        <div className="text-xs text-gray-500 mt-1 truncate" title={def.label}>
          {text}
        </div>
      )
    }
  }
}

function DealCard({
  deal,
  cardCtx,
  onOpen,
  onPreview,
}: {
  deal: Deal
  cardCtx: CardRenderContext
  /** Primary click: open the deal full-screen (same window). */
  onOpen: (id: string) => void
  /** Preview icon: open the deal in the side panel without leaving the board. */
  onPreview: (id: string) => void
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: deal.id })
  const slot = useDroppable({ id: `card:${deal.id}`, disabled: !cardCtx.manualOrder })
  const style = transform
    ? { transform: `translate(${transform.x}px, ${transform.y}px)`, zIndex: 50 }
    : undefined
  return (
    <div
      ref={(el) => {
        setNodeRef(el)
        slot.setNodeRef(el)
      }}
      style={style}
      {...listeners}
      {...attributes}
      onClick={() => onOpen(deal.id)}
      className={`group bg-white rounded-lg border border-gray-200 p-3 mb-2 shadow-sm cursor-grab active:cursor-grabbing ${
        isDragging ? 'opacity-50' : ''
      } ${slot.isOver && !isDragging ? 'border-t-4 border-t-brand-primary' : ''}`}
    >
      {/* The deal's photo, edge to edge over the card — no photo, no gap (cards stay compact). */}
      {cardCtx.showPhoto && deal.coverUrl && (
        <img
          src={deal.coverUrl}
          alt=""
          loading="lazy"
          draggable={false}
          className="-mx-3 -mt-3 mb-2 w-[calc(100%+1.5rem)] max-w-none h-28 object-cover rounded-t-lg"
        />
      )}
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium text-brand-dark">{deal.title}</div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            type="button"
            // stopPropagation so the preview button neither starts a drag nor triggers the full open.
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onPreview(deal.id)
            }}
            title="Preview in side panel"
            aria-label="Preview"
            className="text-gray-300 hover:text-brand-primary opacity-0 group-hover:opacity-100"
          >
            <PanelRight size={14} />
          </button>
          <DealStatusBadge status={deal.status} />
        </div>
      </div>
      {cardCtx.fields.map((fieldId) => (
        <DealCardField key={fieldId} fieldId={fieldId} deal={deal} ctx={cardCtx} />
      ))}
    </div>
  )
}

function StageColumn({
  stage,
  deals,
  cardCtx,
  onAdd,
  onOpen,
  onPreview,
}: {
  stage: Stage
  deals: Deal[]
  cardCtx: CardRenderContext
  onAdd: () => void
  onOpen: (id: string) => void
  onPreview: (id: string) => void
}) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id })
  return (
    // The whole column is the drop target and stretches to the tallest one beside it, so a short
    // column still takes a drop anywhere along its height (not just over its few cards).
    <div ref={setNodeRef} className="w-64 flex-shrink-0 flex flex-col">
      {/* Stays at the top while the board scrolls, so you can see where a dragged deal will land. */}
      <div className="sticky top-0 z-10 flex items-center justify-between mb-2 px-1.5 py-1 rounded-lg bg-white/90 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <span
            className="w-2.5 h-2.5 rounded-full"
            style={{ backgroundColor: stage.color ?? '#94a3b8' }}
          />
          <span className="text-sm font-medium text-brand-dark">{stage.name}</span>
          <span className="text-xs text-gray-400">{deals.length}</span>
        </div>
        <button onClick={onAdd} className="text-gray-400 hover:text-brand-primary" title={`New deal in ${stage.name}`} aria-label={`New deal in ${stage.name}`}>
          <Plus size={16} />
        </button>
      </div>
      <div
        className={`flex-1 min-h-32 rounded-xl p-2 transition-colors ${
          isOver ? 'bg-brand-primary/10' : 'bg-gray-100/70'
        }`}
      >
        {deals.map((d) => (
          <DealCard key={d.id} deal={d} cardCtx={cardCtx} onOpen={onOpen} onPreview={onPreview} />
        ))}
      </div>
      {/* The column's total, as on HubSpot's board footer. */}
      {deals.length > 0 && (
        <div className="px-1 pt-1.5 text-[11px] text-gray-500 tabular-nums">
          Total {formatDealValue(deals.reduce((sum, d) => sum + (d.value ?? 0), 0), cardCtx.currency)}
        </div>
      )}
    </div>
  )
}

/**
 * The column under the pointer wins; only when the pointer is between columns does the card's
 * overlap decide. ("Closest corner" let a tall neighbour steal drops meant for a short column.)
 */
const dropUnderPointer: CollisionDetection = (args) => {
  const under = pointerWithin(args)
  // In "my order" the card under the pointer wins (the deal goes above it) — not the card being dragged.
  const cards = under.filter((c) => String(c.id).startsWith('card:') && c.id !== `card:${args.active.id}`)
  if (cards.length > 0) return cards
  const columns = under.filter((c) => !String(c.id).startsWith('card:'))
  return columns.length > 0 ? columns : rectIntersection(args).filter((c) => !String(c.id).startsWith('card:'))
}

/** A deal untouched this many days gets the amber "Last contact" chip on its card. */
const QUIET_DAYS = 14

/** Day spans offered by the board's "Created" filter. */
const CREATED_PRESETS = [7, 30, 90]

/** A group of stage columns (pipeline section) rendered as one titled block on the board. */
function BoardSection({
  title,
  color,
  stages,
  dealsByStage,
  cardCtx,
  onAdd,
  onOpen,
  onPreview,
}: {
  title: string
  color?: string | null
  stages: Stage[]
  dealsByStage: Map<string, Deal[]>
  cardCtx: CardRenderContext
  onAdd: (stageId: string) => void
  onOpen: (id: string) => void
  onPreview: (id: string) => void
}) {
  let dealCount = 0
  let dealSum = 0
  for (const stage of stages) {
    for (const deal of dealsByStage.get(stage.id) ?? []) {
      dealCount += 1
      dealSum += deal.value ?? 0
    }
  }
  // A saturated pick would overwhelm the board, so tint the group softly (~8% alpha) with the
  // chosen colour and mark the heading with a solid dot of it.
  const tint = color && /^#[0-9a-fA-F]{6}$/.test(color) ? `${color}14` : undefined
  return (
    <section
      className="flex-shrink-0 rounded-2xl border border-gray-200 bg-white/60 p-3"
      style={tint ? { backgroundColor: tint } : undefined}
    >
      <div className="flex items-baseline justify-between gap-6 mb-3 px-1">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-brand-darkest flex items-center gap-1.5">
          {color && (
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
          )}
          {title}
        </h2>
        <span className="text-xs text-gray-500">
          {dealCount} {dealCount === 1 ? 'deal' : 'deals'} · {formatDealValue(dealSum, cardCtx.currency)}
        </span>
      </div>
      <div className="flex items-stretch gap-4">
        {stages.map((stage) => (
          <StageColumn
            key={stage.id}
            stage={stage}
            deals={dealsByStage.get(stage.id) ?? []}
            cardCtx={cardCtx}
            onAdd={() => onAdd(stage.id)}
            onOpen={onOpen}
            onPreview={onPreview}
          />
        ))}
      </div>
    </section>
  )
}

interface StageGroup {
  key: string
  title: string
  color?: string | null
  stages: Stage[]
}

/** Segmented Board | Table view switcher shown in the page header. */
function ViewModeToggle({
  mode,
  onChange,
}: {
  mode: BoardViewMode
  onChange: (mode: BoardViewMode) => void
}) {
  const options: { value: BoardViewMode; label: string; icon: typeof LayoutGrid }[] = [
    { value: 'board', label: 'Board', icon: LayoutGrid },
    { value: 'table', label: 'Table', icon: List },
  ]
  return (
    <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100/70 p-0.5">
      {options.map(({ value, label, icon: Icon }) => (
        <button
          key={value}
          onClick={() => onChange(value)}
          aria-pressed={mode === value}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
            mode === value
              ? 'bg-white text-brand-dark shadow-sm'
              : 'text-gray-500 hover:text-brand-dark'
          }`}
        >
          <Icon size={14} />
          {label}
        </button>
      ))}
    </div>
  )
}

export default function Board() {
  const { data: pipelines, isLoading: loadingPipelines } = usePipelines()
  const { pipeline, select: selectPipeline } = useSelectedPipeline(pipelines)
  const pipelineId = pipeline?.id

  const { data: detail } = usePipelineDetail(pipelineId)
  const { data: deals } = useBoardDeals(pipelineId)
  const move = useMoveDeal(pipelineId)
  const qc = useQueryClient()
  // /deals/:id opens the deal full-screen (deep-link / "open in new tab") and wins over any
  // click; a deal opened by clicking a card or row uses local state and shows as the collapsed
  // side panel. Deriving the open id (no effect) keeps the route the single source of truth.
  const { id: routeDealId } = useParams()
  const navigate = useNavigate()
  const [clickedDealId, setClickedDealId] = useState<string | null>(null)
  const selectedDealId = routeDealId ?? clickedDealId
  // Primary click opens the deal full-screen in the same window (deep-link route); the card/row
  // preview icon opens the collapsed side panel without leaving the board.
  const openFull = (id: string) => navigate(`/deals/${id}`)
  // The deal to prompt for a next contact date after a successful move (null = no prompt).
  const [promptDeal, setPromptDeal] = useState<Deal | null>(null)
  // The New deal dialog: open from the main button (first stage) or a column's "+" (that stage).
  const [newDeal, setNewDeal] = useState<{ stageId?: string } | null>(null)
  // A move waiting on fields its target stage requires (null = none).
  const [gatedMove, setGatedMove] = useState<{ deal: Deal; stage: Stage; missing: Requirement[] } | null>(null)
  const [viewMode, setViewMode] = useState<BoardViewMode>(() =>
    localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'table' ? 'table' : 'board',
  )
  const [boardSort, setBoardSort] = useState<BoardSort>(() =>
    (['created', 'manual'] as const).find((s) => s === localStorage.getItem(BOARD_SORT_STORAGE_KEY)) ?? 'nextContact',
  )
  const [tableSettings, setTableSettings] = useTableSettings()
  const [cardSettings, setCardSettings] = useCardSettings()
  const [search, setSearch] = useState('')
  const [ownerFilter, setOwnerFilter] = useState('')
  const me = useAuthStore((st) => st.user?.agentId)
  const seesOthers = !!me && (deals ?? []).some((d) => d.ownerUserId && d.ownerUserId !== me)
  const [createdAfter, setCreatedAfter] = useState('')
  const [createdBefore, setCreatedBefore] = useState('')
  // "Created" filter as plain choices (last 7/30/90 days); "Custom dates" reveals the two pickers.
  const [customCreated, setCustomCreated] = useState(false)
  const createdPreset = customCreated
    ? 'custom'
    : !createdAfter && !createdBefore
      ? ''
      : (CREATED_PRESETS.find((d) => !createdBefore && createdAfter === isoInDays(-d)) ?? 'custom').toString()

  // Cached by react-query; built into lookup maps once here instead of per card.
  const { data: members } = useMembers()
  const { data: dealFieldDefs } = useFieldDefinitions('deal')
  const currency = useCurrency()
  const cardCtx = useMemo<CardRenderContext>(
    () => ({
      fields: cardSettings.fields,
      showPhoto: cardSettings.showPhoto,
      manualOrder: boardSort === 'manual',
      memberNames: new Map((members ?? []).map((m) => [m.id, m.fullName])),
      fieldDefsByKey: new Map((dealFieldDefs ?? []).map((d) => [d.fieldKey, d])),
      currency,
    }),
    [cardSettings.fields, cardSettings.showPhoto, boardSort, members, dealFieldDefs, currency],
  )

  function changeViewMode(mode: BoardViewMode) {
    setViewMode(mode)
    localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode)
  }

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))

  const groups = useMemo<StageGroup[]>(() => {
    if (!detail) return []
    const result: StageGroup[] = [...detail.sections]
      .sort((a, b) => a.position - b.position)
      .map((section) => ({
        key: section.id,
        title: section.name,
        color: section.color,
        stages: [...section.stages].sort((a, b) => a.position - b.position),
      }))
    if (detail.looseStages.length > 0) {
      result.push({
        key: 'loose-stages',
        title: 'Other',
        stages: [...detail.looseStages].sort((a, b) => a.position - b.position),
      })
    }
    return result.filter((group) => group.stages.length > 0)
  }, [detail])

  // Client-side filters over the deals already loaded for the pipeline (the board endpoint returns
  // them all, so this is complete). Applied before grouping so it filters board + table alike.
  const filteredDeals = useMemo(() => {
    const q = search.trim().toLowerCase()
    return (deals ?? []).filter((d) => {
      if (q && !d.title.toLowerCase().includes(q)) return false
      if (ownerFilter && d.ownerUserId !== ownerFilter) return false
      // Compare the deal's LOCAL creation day (the one the table shows) to the range
      // bounds — slicing the raw UTC instant drifts a day in AU. Both bounds are inclusive.
      const created = localDate(d.createdAt)
      if (createdAfter && created < createdAfter) return false
      if (createdBefore && created > createdBefore) return false
      return true
    })
  }, [deals, search, ownerFilter, createdAfter, createdBefore])

  const dealsByStage = useMemo(() => {
    const map = new Map<string, Deal[]>()
    for (const d of filteredDeals) {
      const arr = map.get(d.stageId) ?? []
      arr.push(d)
      map.set(d.stageId, arr)
    }
    for (const arr of map.values()) arr.sort((a, b) => compareDeals(a, b, boardSort))
    return map
  }, [filteredDeals, boardSort])

  const stagesById = useMemo(() => {
    const map = new Map<string, Stage>()
    for (const group of groups) {
      for (const stage of group.stages) map.set(stage.id, stage)
    }
    return map
  }, [groups])

  /** Deals in board order (by stage position) for the table view. */
  const tableDeals = useMemo(() => {
    const stageOrder = new Map<string, number>()
    let i = 0
    for (const group of groups) {
      for (const stage of group.stages) stageOrder.set(stage.id, i++)
    }
    return [...filteredDeals].sort(
      (a, b) => (stageOrder.get(a.stageId) ?? Infinity) - (stageOrder.get(b.stageId) ?? Infinity),
    )
  }, [filteredDeals, groups])

  function onDragEnd(event: DragEndEvent) {
    const dealId = String(event.active.id)
    const overId = event.over ? String(event.over.id) : null
    if (!overId) return
    // Dropped on a card ("my order"): the deal goes just above it, in that card's column.
    const beforeId = overId.startsWith('card:') ? overId.slice('card:'.length) : null
    const stageId = beforeId ? deals?.find((d) => d.id === beforeId)?.stageId : overId
    const deal = deals?.find((d) => d.id === dealId)
    if (!deal || !stageId) return
    if (boardSort === 'manual') {
      arrange(deal, stageId, beforeId)
    } else if (deal.stageId === stageId) {
      pushToast('To arrange cards by hand, choose “Sort: my order”.', 'success')
      return
    }
    if (deal.stageId !== stageId) {
      const targetStage = stagesById.get(stageId)
      // The stage needs fields this deal lacks: ask for them first, the card stays put until then.
      const missing = targetStage ? missingRequirements(targetStage, deal, dealFieldDefs ?? []) : []
      if (targetStage && missing.length > 0) {
        move.reset()
        setGatedMove({ deal, stage: targetStage, missing })
        return
      }
      moveDeal(deal, stageId)
    }
  }

  /** Put the deal in the column's order (above `beforeId`, or last) and save it as the user's order. */
  function arrange(deal: Deal, stageId: string, beforeId: string | null) {
    const column = (dealsByStage.get(stageId) ?? []).filter((d) => d.id !== deal.id).map((d) => d.id)
    const at = beforeId ? column.indexOf(beforeId) : -1
    column.splice(at < 0 ? column.length : at, 0, deal.id)
    const position = new Map(column.map((id, i) => [id, i]))
    // Show it at once; the board refetch after a stage move brings the saved order back anyway.
    qc.setQueryData<Deal[]>(['deals', 'board', pipelineId], (old) =>
      old?.map((d) => (position.has(d.id) ? { ...d, boardPosition: position.get(d.id) } : d)),
    )
    saveBoardOrder(column).catch((err) => {
      pushToast(apiErrorMessage(err))
      qc.invalidateQueries({ queryKey: ['deals', 'board', pipelineId] })
    })
  }

  function moveDeal(deal: Deal, stageId: string, fills?: MoveFills) {
    const targetStage = stagesById.get(stageId)
    move.mutate(
      { dealId: deal.id, stageId, fills },
      {
        onSuccess: () => {
          setGatedMove(null)
          // Prompt for a next contact date on active/limbo moves; terminal (won/lost) deals need no
          // follow-up, and a date just entered in the requirements dialog needs no second ask.
          if (
            targetStage &&
            targetStage.stageType !== 'won' &&
            targetStage.stageType !== 'lost' &&
            !fills?.nextContactDate
          ) {
            setPromptDeal(deal)
          }
        },
      },
    )
  }

  if (loadingPipelines) return <div className="p-8 text-gray-500">Loading…</div>
  if (!pipeline) return <div className="p-8 text-gray-500">No pipeline yet.</div>

  return (
    // Board and table fill the viewport and scroll inside it, so the sideways scrollbar is always in
    // reach; below md the app's 3rem top bar takes part of it.
    <PageContainer
      width="full"
      className="h-[calc(100dvh-3rem)] md:h-screen flex flex-col overflow-hidden"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 mb-4 shrink-0">
        <div className="flex flex-wrap items-center gap-3">
          <PipelineSwitcher pipelines={pipelines ?? []} pipeline={pipeline} onSelect={selectPipeline} />
          {/* HubSpot's All / My deals views — only for someone who sees other people's deals too. */}
          {seesOthers && me && (
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5" role="group" aria-label="Whose deals">
              {[{ v: '', label: 'All deals' }, { v: me, label: 'My deals' }].map((o) => (
                <button
                  key={o.label}
                  type="button"
                  aria-pressed={ownerFilter === o.v}
                  onClick={() => setOwnerFilter(o.v)}
                  className={`px-3 py-1 text-sm rounded-md ${ownerFilter === o.v ? 'bg-brand-primary text-white' : 'text-gray-600 hover:text-brand-dark'}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search deals…"
            className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40 w-44"
          />
          {viewMode === 'board' && (
            <select
              value={boardSort}
              onChange={(e) => {
                const next = e.target.value as BoardSort
                setBoardSort(next)
                localStorage.setItem(BOARD_SORT_STORAGE_KEY, next)
              }}
              title="Card order within each stage"
              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
            >
              <option value="nextContact">Sort: next contact date</option>
              <option value="created">Sort: deal created date</option>
              <option value="manual">Sort: my order (drag to arrange)</option>
            </select>
          )}
          <select
            value={ownerFilter}
            onChange={(e) => setOwnerFilter(e.target.value)}
            title="Filter by owner"
            className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
          >
            <option value="">All owners</option>
            {(members ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.fullName}
              </option>
            ))}
          </select>
          {/* Created date: plain choices first; exact dates only when asked for (both inclusive). */}
          <select
            value={createdPreset}
            onChange={(e) => {
              const v = e.target.value
              setCustomCreated(v === 'custom')
              if (v === 'custom') return
              setCreatedAfter(v ? isoInDays(-Number(v)) : '')
              setCreatedBefore('')
            }}
            aria-label="Created"
            className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
          >
            <option value="">Created: any time</option>
            {CREATED_PRESETS.map((d) => (
              <option key={d} value={String(d)}>Created: last {d} days</option>
            ))}
            <option value="custom">Created: custom dates…</option>
          </select>
          {createdPreset === 'custom' && (
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <label className="flex items-center gap-1">
                From
                <input
                  type="date"
                  value={createdAfter}
                  max={createdBefore || undefined}
                  onChange={(e) => setCreatedAfter(e.target.value)}
                  className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                />
              </label>
              <label className="flex items-center gap-1">
                to
                <input
                  type="date"
                  value={createdBefore}
                  min={createdAfter || undefined}
                  onChange={(e) => setCreatedBefore(e.target.value)}
                  className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40"
                />
              </label>
            </div>
          )}
          {(search || ownerFilter || createdAfter || createdBefore) && (
            <button
              type="button"
              onClick={() => {
                setSearch('')
                setOwnerFilter('')
                setCreatedAfter('')
                setCreatedBefore('')
                setCustomCreated(false)
              }}
              className="text-xs text-gray-500 hover:text-brand-dark"
            >
              Clear
            </button>
          )}
          <ViewModeToggle mode={viewMode} onChange={changeViewMode} />
          {viewMode === 'board' && (
            <CardSettingsPopover settings={cardSettings} onChange={setCardSettings} />
          )}
          {viewMode === 'table' && (
            <TableSettingsPopover settings={tableSettings} onChange={setTableSettings} fieldDefs={dealFieldDefs ?? []} />
          )}
          <button
            type="button"
            onClick={() => setNewDeal({})}
            className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark"
          >
            <Plus size={15} /> New deal
          </button>
        </div>
      </div>
      {viewMode === 'board' ? (
        <DndContext sensors={sensors} collisionDetection={dropUnderPointer} onDragEnd={onDragEnd}>
          {/* flex-1 + overflow-auto: the board fills the remaining viewport height, so its
              horizontal scrollbar sits at the bottom of the screen (always reachable) instead
              of below the tallest column; taller content scrolls vertically inside this region. */}
          <div className="flex-1 min-h-0 overflow-auto pb-2">
            <div className="flex items-start gap-6">
              {groups.map((group) => (
              <BoardSection
                key={group.key}
                title={group.title}
                color={group.color}
                stages={group.stages}
                dealsByStage={dealsByStage}
                cardCtx={cardCtx}
                onAdd={(stageId) => setNewDeal({ stageId })}
                onOpen={openFull}
                onPreview={setClickedDealId}
              />
              ))}
            </div>
          </div>
        </DndContext>
      ) : (
        <DealTable
          deals={tableDeals}
          stagesById={stagesById}
          onOpen={openFull}
          onPreview={setClickedDealId}
          settings={tableSettings}
          onSettingsChange={setTableSettings}
          currency={currency}
          fieldDefs={dealFieldDefs ?? []}
        />
      )}

      {selectedDealId && (
        <DealDetail
          dealId={selectedDealId}
          pipelineId={pipelineId}
          defaultExpanded={!!routeDealId}
          onClose={() => {
            // Arrived via /deals/:id — return to the board so the URL no longer pins this deal;
            // otherwise it was a click-opened side panel, so just clear local state.
            if (routeDealId) navigate('/', { replace: true })
            else setClickedDealId(null)
          }}
        />
      )}

      {newDeal && (
        <NewDealModal
          pipelineId={pipelineId}
          stageId={newDeal.stageId}
          onClose={() => setNewDeal(null)}
          onCreated={(created) => {
            setNewDeal(null)
            setClickedDealId(created.id) // open it straight away to fill in the rest
          }}
        />
      )}

      {gatedMove && (
        <StageRequirementsDialog
          deal={gatedMove.deal}
          stage={gatedMove.stage}
          missing={gatedMove.missing}
          pending={move.isPending}
          error={move.isError ? apiErrorMessage(move.error) : null}
          onMove={(fills) => moveDeal(gatedMove.deal, gatedMove.stage.id, fills)}
          onClose={() => setGatedMove(null)}
        />
      )}

      {promptDeal && (
        <NextContactDatePrompt
          deal={promptDeal}
          pipelineId={pipelineId}
          onClose={() => setPromptDeal(null)}
        />
      )}
    </PageContainer>
  )
}
